import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  teacherDisplayName,
  useBindLoggedInToTeacher,
  useTeacherInvite,
} from '../hooks/useTeacherInvite';
import RegisterForm from './RegisterForm';

export default function TeacherJoinPage() {
  const { teacherParam, teacherInfo, teacherWarning, teacherLoading } = useTeacherInvite();
  const { user, authLoading, binding, bindError, isOwnLink } = useBindLoggedInToTeacher(teacherInfo);
  const [mode, setMode] = useState(null);
  const teacherName = teacherDisplayName(teacherInfo);

  if (authLoading || teacherLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Загрузка...</p>
      </div>
    );
  }

  if (user && teacherParam && !teacherInfo) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h2>Привязка к преподавателю</h2>
          {teacherWarning && <div className="alert alert-error">{teacherWarning}</div>}
          <p className="auth-subtitle">Вы уже вошли как {user.username}.</p>
          <Link to="/dashboard" className="btn btn-primary btn-full">На главную</Link>
        </div>
      </div>
    );
  }

  if (user && isOwnLink) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h2>Ваша ссылка для учеников</h2>
          <p className="auth-subtitle">
            Отправьте её ученику. Новый аккаунт зарегистрируется и привяжется,
            у кого аккаунт уже есть — только привяжется.
          </p>
          <Link to="/teacher" className="btn btn-primary btn-full">В кабинет</Link>
        </div>
      </div>
    );
  }

  if (user && teacherInfo) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h2>Привязка к преподавателю</h2>
          {bindError && <div className="alert alert-error">{bindError}</div>}
          <p className="auth-subtitle">
            {binding
              ? `Привязываем к преподавателю ${teacherName}…`
              : `Вы вошли как ${user.username}.`}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h2>Присоединиться к преподавателю</h2>
        <p className="auth-subtitle">
          {teacherName
            ? `Одна ссылка: новый ученик регистрируется, у кого уже есть аккаунт — входит. Преподаватель: ${teacherName}${teacherInfo?.username ? ` (@${teacherInfo.username})` : ''}.`
            : 'Создайте аккаунт или войдите, если он уже есть.'}
        </p>
        {teacherWarning && (
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
            {teacherWarning}
          </div>
        )}

        {mode == null && (
          <div className="auth-join-actions">
            <button type="button" className="btn btn-primary btn-full" onClick={() => setMode('register')}>
              Нет аккаунта — зарегистрироваться
            </button>
            <button type="button" className="btn btn-outline btn-full" onClick={() => setMode('login')}>
              Уже есть аккаунт — войти и привязаться
            </button>
          </div>
        )}

        {mode === 'register' && (
          <>
            <p className="auth-mode-back">
              <button type="button" className="btn btn-outline btn-xs" onClick={() => setMode(null)}>
                Назад
              </button>
            </p>
            <RegisterForm teacherUsername={teacherInfo?.username} />
          </>
        )}

        {mode === 'login' && (
          <>
            <p className="auth-mode-back">
              <button type="button" className="btn btn-outline btn-xs" onClick={() => setMode(null)}>
                Назад
              </button>
            </p>
            <JoinLoginForm teacherUsername={teacherInfo?.username} />
            <p className="auth-link">
              Нет аккаунта?{' '}
              <button type="button" className="auth-inline-link" onClick={() => setMode('register')}>
                Зарегистрироваться
              </button>
            </p>
          </>
        )}

        {mode == null && teacherParam && (
          <p className="auth-link">
            <Link to="/login">Обычный вход без привязки</Link>
          </p>
        )}
      </div>
    </div>
  );
}

function JoinLoginForm({ teacherUsername }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const extra = teacherUsername ? { teacher: teacherUsername } : {};
      await login(username, password, extra);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Неверные учётные данные');
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="form-group">
        <label htmlFor="join_username">Имя пользователя</label>
        <input
          id="join_username"
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Введите имя пользователя"
          required
          autoFocus
        />
      </div>
      <div className="form-group">
        <label htmlFor="join_password">Пароль</label>
        <input
          id="join_password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Введите пароль"
          required
        />
      </div>
      <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
        {loading ? 'Вход...' : 'Войти и привязаться'}
      </button>
    </form>
  );
}
